import React, { useEffect, useMemo } from "react";
import {
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import {
  createAddOnMaster,
  updateAddOnMaster,
} from "../../../store/slices/clientsSlice";

// Keys 4iq's monitoring integration (cyberior-4iq/4IQ/monitoring.js) actually
// recognizes and forwards to the real 4IQ API - a typo here silently drops the
// field from monitoring instead of erroring, so this is a closed list rather
// than free text.
const KNOWN_SERVICE_KEYS = [
  { key: "emailAddress", label: "Email Address" },
  { key: "mobile", label: "Mobile" },
  { key: "pan", label: "PAN Card" },
  { key: "drivingLicenseNumber", label: "Driving License Number" },
  { key: "aadharCardNumber", label: "Aadhar Card Number" },
  { key: "passportNumber", label: "Passport Number" },
  { key: "healthCardNumber", label: "Health Card Number" },
  { key: "vehicalInsuranceNumber", label: "Vehicle Insurance Number" },
  { key: "bankAccount", label: "Bank Account" },
  { key: "creditCard", label: "Credit Card" },
  { key: "loyaltyAndFrequentFlyerCard", label: "Loyalty/Frequent Flyer Card" },
  { key: "prepaidCard", label: "Prepaid Card" },
  { key: "smallBusinessDomainUrl", label: "Small Business Domain URL" },
  { key: "usernameOrSocialMediaHandle", label: "Username or Social Media Handle" },
  { key: "googleProfile", label: "Google Profile" },
  { key: "facebookProfile", label: "Facebook Profile" },
  { key: "twitterProfile", label: "Twitter Profile" },
  { key: "youtubeProfile", label: "YouTube Profile" },
  { key: "instagramProfile", label: "Instagram Profile" },
  { key: "linkedinProfile", label: "LinkedIn Profile" },
  { key: "githubProfile", label: "GitHub Profile" },
];

const AddAddOnServiceModal = ({ isOpen, toggle, editItem }) => {
  const dispatch = useDispatch();
  const isEdit = editItem && Object.keys(editItem).length > 0;

  const defaultValues = useMemo(() => {
    if (isEdit) {
      return {
        serviceName: editItem.serviceName || "",
        serviceKey: editItem.serviceKey || "",
        maxSlot: editItem.maxSlot ?? "",
        defaultFree: editItem.defaultFree ?? "",
        extraFree: editItem.extraFree ?? "",
        purchaseSLot: editItem.purchaseSLot ?? "",
        isEnable: editItem.isEnable ?? true,
        personal_identity: editItem.personal_identity ?? false,
        financial_identity: editItem.financial_identity ?? false,
        digital_itendity: editItem.digital_itendity ?? false,
      };
    }
    return {
      serviceName: "",
      serviceKey: "",
      maxSlot: "",
      defaultFree: "",
      extraFree: "",
      purchaseSLot: "",
      isEnable: true,
      personal_identity: false,
      financial_identity: false,
      digital_itendity: false,
    };
  }, [editItem, isEdit]);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({ defaultValues });

  // If an existing row's serviceKey isn't in the known-good list (legacy data,
  // typo, etc.), keep it selectable so editing the row doesn't silently wipe it.
  const serviceKeyOptions = useMemo(() => {
    const existingKey = editItem?.serviceKey;
    if (existingKey && !KNOWN_SERVICE_KEYS.some((opt) => opt.key === existingKey)) {
      return [...KNOWN_SERVICE_KEYS, { key: existingKey, label: `${existingKey} (unrecognized)` }];
    }
    return KNOWN_SERVICE_KEYS;
  }, [editItem]);

  useEffect(() => {
    if (isOpen) {
      reset(defaultValues);
    }
  }, [isOpen, reset, defaultValues]);

  const onSubmit = async (data) => {
    const payload = {
      serviceName: data.serviceName,
      serviceKey: data.serviceKey || undefined,
      maxSlot: data.maxSlot === "" ? undefined : Number(data.maxSlot),
      defaultFree: data.defaultFree === "" ? undefined : Number(data.defaultFree),
      extraFree: data.extraFree === "" ? undefined : Number(data.extraFree),
      purchaseSLot: data.purchaseSLot === "" ? undefined : Number(data.purchaseSLot),
      isEnable: !!data.isEnable,
      personal_identity: !!data.personal_identity,
      financial_identity: !!data.financial_identity,
      digital_itendity: !!data.digital_itendity,
    };

    try {
      if (isEdit) {
        await dispatch(
          updateAddOnMaster({ id: editItem.ID, payload }),
        );
      } else {
        await dispatch(createAddOnMaster({ ...payload, isMaster: true }));
      }
      toggle();
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader>
        {isEdit ? "Edit Add-on Service" : "Add Add-on Service"}
      </ModalHeader>
      <ModalBody>
        <form id="addon-service-form" onSubmit={handleSubmit(onSubmit)}>
          <div className="px-2">
            <div className="d-flex gap-2 mt-2">
              <div className="w-100">
                <Label>Service Name</Label>
                <input
                  type="text"
                  className={`form-control ${errors.serviceName ? "is-invalid" : ""}`}
                  placeholder="Enter Service Name"
                  {...register("serviceName", { required: "Service Name is required" })}
                />
                {errors.serviceName && (
                  <span className="text-danger small">{errors.serviceName.message}</span>
                )}
              </div>
              <div className="w-100">
                <Label>Service Key</Label>
                <select className="form-control" {...register("serviceKey")}>
                  <option value="">Select Service Key</option>
                  {serviceKeyOptions.map((opt) => (
                    <option key={opt.key} value={opt.key}>
                      {opt.label} ({opt.key})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="d-flex gap-2 mt-3">
              <div className="w-100">
                <Label>Max Slot</Label>
                <input
                  type="number"
                  className="form-control"
                  placeholder="Max Slot"
                  {...register("maxSlot")}
                />
              </div>
              <div className="w-100">
                <Label>Default Free</Label>
                <input
                  type="number"
                  className="form-control"
                  placeholder="Default Free"
                  {...register("defaultFree")}
                />
              </div>
            </div>

            <div className="d-flex gap-2 mt-3">
              <div className="w-100">
                <Label>Extra Free</Label>
                <input
                  type="number"
                  className="form-control"
                  placeholder="Extra Free"
                  {...register("extraFree")}
                />
              </div>
              <div className="w-100">
                <Label>Purchase Slot</Label>
                <input
                  type="number"
                  className="form-control"
                  placeholder="Purchase Slot"
                  {...register("purchaseSLot")}
                />
              </div>
            </div>

            <div className="d-flex gap-4 mt-3 flex-wrap">
              <div className="form-check">
                <Input type="checkbox" className="form-check-input" id="isEnable" {...register("isEnable")} />
                <Label className="form-check-label" for="isEnable">Enabled</Label>
              </div>
              <div className="form-check">
                <Input type="checkbox" className="form-check-input" id="personal_identity" {...register("personal_identity")} />
                <Label className="form-check-label" for="personal_identity">Personal Identity</Label>
              </div>
              <div className="form-check">
                <Input type="checkbox" className="form-check-input" id="financial_identity" {...register("financial_identity")} />
                <Label className="form-check-label" for="financial_identity">Financial Identity</Label>
              </div>
              <div className="form-check">
                <Input type="checkbox" className="form-check-input" id="digital_itendity" {...register("digital_itendity")} />
                <Label className="form-check-label" for="digital_itendity">Digital Identity</Label>
              </div>
            </div>
          </div>
        </form>
      </ModalBody>
      <ModalFooter>
        <button className="btn btn-primary" onClick={handleSubmit(onSubmit)}>
          {isEdit ? "Update" : "Create"}
        </button>
        <button className="btn btn-danger" onClick={toggle}>Cancel</button>
      </ModalFooter>
    </Modal>
  );
};

export default AddAddOnServiceModal;
